import React, { useEffect, useRef, useState } from "react";
import { Lock, Unlock, Download, FileText, Loader2, ShieldCheck, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";

const OTP_STORAGE_PREFIX = "otp_verified_";

export default function AttachmentsUnlock({ propertyId, anonymous = false, unlockKeyStorage }) {
  const storedEmail = anonymous && unlockKeyStorage ? (localStorage.getItem(unlockKeyStorage) || "") : "";
  const [email, setEmail] = useState(storedEmail);
  const emailVerifiedFlag = anonymous ? !!localStorage.getItem(OTP_STORAGE_PREFIX + storedEmail.toLowerCase()) : true;
  const [verified, setVerified] = useState(emailVerifiedFlag);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef();

  const params = anonymous && verified && email ? { buyer_email: email } : {};

  const load = async () => {
    try {
      const s = await api.get(`/properties/${propertyId}/unlock-status`, { params });
      setStatus(s.data);
      if (s.data.unlocked) {
        const a = await api.get(`/properties/${propertyId}/attachments`, { params });
        setItems(a.data.items || []);
      } else setItems([]);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId, email, verified]);

  const startCooldown = (secs) => {
    setCooldown(secs);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCooldown((c) => (c <= 1 ? (clearInterval(timerRef.current), 0) : c - 1)), 1000);
  };

  const sendOtp = async () => {
    if (!email) { toast.error("Inserisci la tua email"); return; }
    setBusy(true);
    try {
      await api.post("/otp/send", { email, property_id: propertyId });
      setOtpSent(true);
      startCooldown(60);
      toast.success("Codice inviato via email");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (otpCode.length < 6) return;
    setBusy(true);
    try {
      await api.post("/otp/verify", { email, code: otpCode });
      setVerified(true);
      if (unlockKeyStorage) {
        localStorage.setItem(unlockKeyStorage, email);
        localStorage.setItem(OTP_STORAGE_PREFIX + email.toLowerCase(), "1");
      }
      toast.success("Email verificata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startCheckout = async () => {
    setBusy(true);
    try {
      const body = { property_id: propertyId, origin_url: window.location.origin };
      if (anonymous) body.buyer_email = email;
      const { data } = await api.post("/payments/checkout", body);
      if (data.already_unlocked) { toast.success("Allegati già sbloccati."); await load(); return; }
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const download = async (att) => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/attachments/${att.id}/download`, { params });
      const link = document.createElement("a");
      link.href = data.download_url.startsWith("/api") ? `${API}${data.download_url.slice(4)}` : data.download_url;
      link.download = data.filename || att.filename || "allegato";
      link.target = "_blank"; link.rel = "noopener";
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!status) return <Card className="border-border/60 p-6 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></Card>;

  if (status.unlocked) {
    return (
      <Card className="border-emerald-500/40 bg-emerald-500/[0.03] p-6" data-testid="attachments-unlocked">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <div>
            <h3 className="font-heading text-lg font-medium">Allegati sbloccati</h3>
            <p className="text-xs text-muted-foreground">Scarica i documenti dell'opportunità in qualsiasi momento.</p>
          </div>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Nessun allegato disponibile per questa opportunità.</p>
        ) : (
          <div className="space-y-2">
            {items.map((att) => (
              <div key={att.id} className="flex items-center justify-between p-3 rounded-md border border-border/60 hover:border-primary/40 hover:bg-muted/40 transition-colors" data-testid={`attachment-${att.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-primary shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{att.name}</p>
                    {att.filename && att.filename !== att.name && (<p className="text-xs text-muted-foreground truncate">{att.filename}</p>)}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => download(att)} data-testid={`download-${att.id}`}>
                  <Download className="w-3.5 h-3.5" /> Scarica
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card className="border-border/60 p-6 relative overflow-hidden" data-testid="attachments-locked">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
      <div className="relative">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-medium">Sblocca gli allegati</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Accedi al pacchetto documentale completo: visura, planimetria, APE, atto, contratti, ordinanza, perizia, avviso.
            </p>
          </div>
        </div>

        <div className="my-5 flex items-baseline gap-2">
          <span className="font-heading text-4xl font-semibold tracking-tight tabular-nums" data-testid="unlock-price">{status.price?.display || "€ 69,00"}</span>
          <span className="text-sm text-muted-foreground">una tantum · accesso permanente</span>
        </div>

        {anonymous && !verified && (
          <div className="mb-4 space-y-3 rounded-md border border-dashed border-border/60 p-4 bg-muted/30">
            {!otpSent ? (
              <>
                <div>
                  <Label htmlFor="unlock-email" className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> La tua email</Label>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-2">Ti inviamo un codice per confermare l'identità prima del pagamento.</p>
                  <Input id="unlock-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="mario.rossi@esempio.it" data-testid="unlock-email-input" />
                </div>
                <Button className="w-full gap-2" onClick={sendOtp} disabled={busy || !email} data-testid="send-otp-button">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> Ricevi codice via email</>}
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Codice di verifica</Label>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-3">Abbiamo inviato un codice a 6 cifre a <strong className="text-foreground">{email}</strong> (scade in 10 min).</p>
                  <div className="flex justify-center">
                    <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode} data-testid="otp-input">
                      <InputOTPGroup>
                        {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={sendOtp} disabled={busy || cooldown > 0} data-testid="resend-otp-button" className="flex-1">
                    {cooldown > 0 ? `Nuovo codice in ${cooldown}s` : "Rinvia codice"}
                  </Button>
                  <Button size="sm" onClick={verifyOtp} disabled={busy || otpCode.length !== 6} data-testid="verify-otp-button" className="flex-1 gap-1">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Conferma <ArrowRight className="w-3.5 h-3.5" /></>}
                  </Button>
                </div>
                <button className="text-xs text-muted-foreground hover:text-foreground w-full text-center" onClick={() => { setOtpSent(false); setOtpCode(""); }}>← Usa un'altra email</button>
              </>
            )}
          </div>
        )}

        {anonymous && verified && (
          <p className="text-xs text-emerald-600 flex items-center gap-1 mb-3"><ShieldCheck className="w-3.5 h-3.5" /> Email verificata: {email}</p>
        )}

        <Button className="w-full gap-2" onClick={startCheckout} disabled={busy || (anonymous && !verified)} data-testid="unlock-checkout-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
          {busy ? "Reindirizzamento…" : `Sblocca ora · ${status.price?.display || "€ 69,00"}`}
        </Button>

        <p className="text-xs text-muted-foreground mt-3 text-center">
          Pagamento sicuro con Stripe · Carta di credito, Apple Pay, Google Pay
        </p>
      </div>
    </Card>
  );
}
